import { type FunctionComponent, useState } from 'react';
import {
  EuiComboBox,
  type EuiComboBoxOptionOption,
  EuiText,
} from '@elastic/eui';
import type { StructureWithParents } from '@lib/structures';
import useStructureSearch from '@data/structures';
import { structConf } from '@lib/structures';

type StructureOption = EuiComboBoxOptionOption<StructureWithParents>;

const structureOption = (structure: StructureWithParents): StructureOption => ({
  label: `${structConf[structure.type].displayTitle}: ${structure.name}`,
  value: structure,
});

const renderOption = (option: StructureOption) => {
  const value = option.value as StructureWithParents;

  const province = value.parents.find(parent => parent.type === 'province');

  const descrip =
    province == null
      ? structConf[value.type].displayTitle
      : `${structConf[value.type].displayTitle}, ${province.name}`;

  return (
    <div>
      <EuiText size="xs">
        {value.name} ({value.code})
      </EuiText>
      <EuiText size="xs" color="subdued">
        {descrip}
      </EuiText>
    </div>
  );
};

interface StructureSearchProps {
  values: StructureWithParents[];
  onChange: (values: StructureWithParents[]) => void;
  disabled?: boolean;
}

const StructureSearch: FunctionComponent<StructureSearchProps> = props => {
  const { values, onChange, disabled = false } = props;

  const [searchValue, setSearchValue] = useState('');

  const trimmed = searchValue.trim();
  const { loading, structures } = useStructureSearch(trimmed);

  const options: StructureWithParents[] = structures ?? [
    { type: 'country', code: 'ZA', name: 'South Africa', parents: [] },
  ];

  const handleChange = (options: StructureOption[]) => {
    onChange(options.map(o => o.value as StructureWithParents));
  };

  return (
    <EuiComboBox
      fullWidth
      async
      isLoading={loading}
      isDisabled={disabled}
      aria-label="Structure search"
      placeholder="Search for a structure"
      singleSelection={false}
      options={options.map(structureOption)}
      selectedOptions={values.map(structureOption)}
      onChange={handleChange}
      renderOption={renderOption}
      rowHeight={50}
      onSearchChange={setSearchValue}
      isClearable
    />
  );
};

export default StructureSearch;
